'use client';

import { LoadingFallback } from '@order-up/common';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  adoptCustomTokenOrGuestSession,
  getActiveGuestSession,
  storeGuestSession,
} from '../services/guestSession';
import { getAppCheckHealth } from '../firebase/appCheck';
import { getFirebaseAuth } from '../firebase/config';
import { useAuth, useSignInWithToken, useSignOut } from '../hooks/useAuth';
import { getApiUrl, getAutomationBypassSecret } from '../utils/env';
import { createClientLogger } from '../utils/logger';

const logger = createClientLogger('CustomerAuthHandler');

/** The URL-fragment key the menu page puts the hand-off code under (`getCheckoutLink`). */
const HANDOFF_FRAGMENT_KEY = 'handoff';

/**
 * A redeemed hand-off kept ONLY across the one reload an App Check refusal
 * triggers: the code is single-use, so a reload cannot redeem it again, and
 * without this the guest would land on "Access Denied" for a refusal that was
 * ours. Cleared the moment the sign-in settles either way.
 */
const HANDOFF_STASH_KEY = 'orderup.checkoutHandoff';

interface HandoffStash {
  code: string;
  customToken: string;
  uid: string;
  /**
   * The same uid as a guest session (present while guest sessions are on), for
   * a checkout browser App Check refuses: it cannot sign in with the custom
   * token, so it pays on the session instead (services/guestSession.ts).
   */
  guestSession?: string;
  guestSessionExpiresAt?: number;
}

function readHandoffStash(code: string): HandoffStash | null {
  try {
    const raw = window.sessionStorage.getItem(HANDOFF_STASH_KEY);
    if (!raw) return null;
    const stash = JSON.parse(raw) as HandoffStash;
    return stash.code === code ? stash : null;
  } catch {
    return null;
  }
}

function writeHandoffStash(stash: HandoffStash | null): void {
  try {
    if (stash) window.sessionStorage.setItem(HANDOFF_STASH_KEY, JSON.stringify(stash));
    else window.sessionStorage.removeItem(HANDOFF_STASH_KEY);
  } catch {
    // Storage unavailable (private mode): the reload path simply cannot resume.
  }
}

interface CustomerAuthHandlerProps {
  children: React.ReactNode;
  /** URL parameter name for session token (default: 'session') */
  sessionParamName?: string;
  /** API URL override */
  apiUrl?: string;
  /** Loading component */
  loadingComponent?: React.ComponentType;
  /**
   * Optional callback to validate if existing session should be replaced.
   * Return true to sign out and use new token, false to keep existing session.
   * If not provided, always signs out existing sessions (default behavior).
   *
   * Use case: Validate cart/order ownership before replacing session.
   *
   * @param currentUid - UID of currently authenticated user
   * @param sessionToken - New session token from URL
   * @returns Promise<boolean> - true to sign out, false to keep session
   *
   * @example
   * ```tsx
   * shouldSignOut={async (currentUid) => {
   *   const cart = await loadCart(cartId);
   *   return currentUid !== cart?.customerId; // Sign out only if different user
   * }}
   * ```
   */
  shouldSignOut?: (currentUid: string, sessionToken: string) => Promise<boolean>;
  /** Callback when auth completes */
  onComplete?: (uid: string) => void;
  /** Callback when auth fails */
  onError?: (error: Error) => void;
}

/**
 * Handles cross-app authentication for customer-facing applications.
 *
 * This component is designed for PUBLIC/CUSTOMER apps (checkout, ordering)
 * where users don't belong to a tenant but need auth continuity across TLDs.
 *
 * Flow (guest checkout):
 * 1. Menu page, signed in as the guest: `POST /auth/checkout-handoff` → a
 *    one-time code bound to the cart (two minutes, single use)
 * 2. Redirect: checkout…/{slug}?cart=<guid>#handoff=<code> — in the FRAGMENT,
 *    which no server and no Referer ever sees
 * 3. This component redeems the code once (`/auth/checkout-handoff/redeem`,
 *    with the cart from `?cart=`) for a custom token for the SAME uid
 * 4. Signs in with it, unless that guest is already signed in here
 * 5. Removes the code from the address bar
 *
 * Legacy: a link carrying `?session=<idToken>` (menu pages built before the
 * hand-off code) is still exchanged through `/auth/exchange-token`. Nothing
 * this repo builds emits one any more; delete that branch once old menu pages
 * are gone.
 *
 * Difference from TokenHandler:
 * - TokenHandler: For internal apps (management/admin) - creates session cookies
 * - CustomerAuthHandler: For customer apps - just exchanges tokens (works across TLDs)
 *
 * @example
 * ```tsx
 * <ClientProviders>
 *   <CustomerAuthHandler sessionParamName="session">
 *     {children}
 *   </CustomerAuthHandler>
 * </ClientProviders>
 * ```
 */
export function CustomerAuthHandler({
  children,
  sessionParamName = 'session',
  apiUrl,
  loadingComponent: LoadingComponent = LoadingFallback,
  shouldSignOut,
  onComplete,
  onError
}: CustomerAuthHandlerProps) {
  // Extract session token from URL (memoized to avoid re-reading on every render)
  const sessionToken = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(sessionParamName);
  }, [sessionParamName]);

  // The one-time hand-off code (fragment) and the cart it was issued for.
  const handoff = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const code = new URLSearchParams(window.location.hash.replace(/^#/, '')).get(HANDOFF_FRAGMENT_KEY);
    if (!code) return null;
    const cartGuid = new URLSearchParams(window.location.search).get('cart') ?? '';
    return { code, cartGuid };
  }, []);
  const hasCredential = sessionToken !== null || handoff !== null;

  // A token in the URL means an exchange is coming, so we are processing from
  // the FIRST render. Starting at `false` left one render in which the check
  // was complete and processing had not begun yet — the children mounted,
  // ran their effects before ours, and any API call they made on mount went
  // out with no Firebase user and so no bearer (the POS keyed-entry page's
  // PayPage mint 401'd exactly that way in production, 2026-09-11).
  const [isProcessing, setIsProcessing] = useState(() => hasCredential);
  const [error, setError] = useState<string | null>(null);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const hasProcessedRef = useRef(false);
  const isProcessingTokenRef = useRef(false); // Track if we're in the middle of token processing

  const signInWithTokenMutation = useSignInWithToken();
  const signOutMutation = useSignOut();
  const { data: authData } = useAuth();
  const currentUser = authData?.authUser;

  // Wait for auth state to be determined (either user exists or confirmed no user)
  // before running the security checks. We key off the Firebase auth listener's
  // FIRST emission — the definitive "persisted session restored (or confirmed
  // none)" signal — rather than a fixed timer. A fixed delay was a race: too
  // short denied a valid persisted session on a slow/cold load (false "Access
  // Denied"); too long needlessly stalled fast loads. A long safety timeout
  // still releases the gate if the listener somehow never emits.
  useEffect(() => {
    if (authCheckComplete) return;

    if (hasCredential) {
      // Has a credential - will process it, mark as complete immediately
      setAuthCheckComplete(true);
      return;
    }

    const auth = getFirebaseAuth();
    // Already restored synchronously — no need to wait.
    if (auth.currentUser) {
      setAuthCheckComplete(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, () => {
      logger.debug('Firebase auth resolved', { currentUser: auth.currentUser?.uid || 'none' });
      setAuthCheckComplete(true);
    });
    // Safety net: never hang the page if the listener never emits.
    const timer = setTimeout(() => setAuthCheckComplete(true), 5000);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [authCheckComplete, hasCredential]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!authCheckComplete) return; // Wait for auth check to complete before security checks

    // Security: Block access if no session token AND no existing authenticated user.
    // Consult the LIVE Firebase currentUser in addition to the React Query cache:
    // `useAuthSubscription` populates the cache asynchronously, so right after the
    // auth listener resolves the cache may briefly lag behind Firebase. Using the
    // live value here closes that window so a valid persisted session is never
    // denied. (Skip while a token exchange is in flight.)
    const liveFirebaseUser = getFirebaseAuth().currentUser;
    if (!hasCredential && !currentUser && !liveFirebaseUser && !getActiveGuestSession() && !isProcessingTokenRef.current) {
      logger.warn('Access denied: No session token and no authenticated user');
      setError('You must access this page from the restaurant menu. Direct access is not permitted.');
      return;
    }

    // The one-time hand-off code: redeem it once, sign in as that guest.
    if (handoff) {
      if (hasProcessedRef.current) return;
      hasProcessedRef.current = true;
      isProcessingTokenRef.current = true;
      setIsProcessing(true);

      // Remove the code once the sign-in has SETTLED. An App Check refusal
      // reloads the page once; the code is spent by then, so the redeemed
      // token waits in the stash for that reload and the fragment stays.
      const settle = (keepForReload: boolean) => {
        if (keepForReload && getAppCheckHealth() === 'refused') return;
        writeHandoffStash(null);
        if (!window.location.hash) return;
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        fragment.delete(HANDOFF_FRAGMENT_KEY);
        const rest = fragment.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + window.location.search + (rest ? `#${rest}` : ''),
        );
      };

      const processHandoff = async () => {
        try {
          let redeemed = readHandoffStash(handoff.code);
          if (!redeemed) {
            const baseUrl = apiUrl || getApiUrl();
            const bypassSecret = getAutomationBypassSecret();
            const response = await fetch(`${baseUrl}/api/v1/auth/checkout-handoff/redeem`, { // wire-fold-ok: auth hand-off, reads only data.customToken/uid
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(bypassSecret && { 'x-vercel-protection-bypass': bypassSecret }),
              },
              body: JSON.stringify({ code: handoff.code, cartGuid: handoff.cartGuid }),
            });
            if (!response.ok) {
              throw new Error('This checkout link has expired. Return to the menu and check out again.');
            }
            const data = await response.json();
            redeemed = {
              code: handoff.code,
              customToken: data.data.customToken,
              uid: data.data.uid,
              guestSession: data.data.guestSession,
              guestSessionExpiresAt: data.data.guestSessionExpiresAt,
            };
          }

          // Already signed in here as that same guest: nothing to swap.
          if (currentUser?.uid !== redeemed.uid) {
            if (currentUser) {
              logger.debug('Signing out a different user before the hand-off sign-in', { uid: currentUser.uid });
              await signOutMutation.mutateAsync();
            }
            writeHandoffStash(redeemed);
            const session =
              redeemed.guestSession && typeof redeemed.guestSessionExpiresAt === 'number'
                ? { uid: redeemed.uid, token: redeemed.guestSession, expiresAt: redeemed.guestSessionExpiresAt }
                : null;
            if (session) {
              // The guest session is this page's identity the moment it is
              // adopted (the SAME uid), so checkout goes on NOW: the cart and
              // the total must not wait on a Firebase sign-in that a refused
              // browser stalls for the whole timeout (~10 s before the total
              // showed, prod 2026-10-12). The sign-in finishes in the
              // background and hands over to the Firebase user; the code is
              // settled only then, so an App Check refusal reload in between
              // still finds the redeemed token in the stash.
              storeGuestSession(session);
              const uid = redeemed.uid;
              void adoptCustomTokenOrGuestSession(redeemed.customToken, session, signInWithTokenMutation.mutateAsync)
                .then((outcome) => logger.debug('Checkout hand-off identity', { uid, outcome }))
                .catch((err) => logger.warn('Checkout hand-off sign-in failed; staying on the guest session', { uid, error: err instanceof Error ? err.message : String(err) }))
                .finally(() => settle(false));
              logger.debug('Checkout hand-off running on the guest session', { uid });
              setIsProcessing(false);
              isProcessingTokenRef.current = false;
              onComplete?.(uid);
              return;
            }
            await signInWithTokenMutation.mutateAsync(redeemed.customToken);
          }

          logger.debug('Checkout hand-off sign-in complete', { uid: redeemed.uid });
          settle(false);
          setIsProcessing(false);
          isProcessingTokenRef.current = false;
          onComplete?.(redeemed.uid);
        } catch (err) {
          logger.error('Checkout hand-off failed', err);
          settle(true);
          setError(err instanceof Error ? err.message : 'Authentication failed');
          setIsProcessing(false);
          isProcessingTokenRef.current = false;
          onError?.(err instanceof Error ? err : new Error(String(err)));
        }
      };

      void processHandoff();
      return;
    }

    // If user is already authenticated and no new token, skip processing
    if (!sessionToken) {
      if (currentUser) {
        logger.debug('User already authenticated', { uid: currentUser.uid });
      }
      return;
    }

    // Only process token once per session
    if (sessionToken && !hasProcessedRef.current) {
      logger.debug('Processing cross-app session token');
      setIsProcessing(true);
      hasProcessedRef.current = true;
      isProcessingTokenRef.current = true; // Mark as processing to prevent security check

      // Remove the session token from the address bar once the exchange has
      // SETTLED — not before. An App Check refusal reloads the page once
      // (enableAppCheckRefusalRecovery), and a reload after an early strip
      // came back with no token and no user: "Access Denied" for a guest who
      // did everything right. Kept only while a refusal reload is pending.
      const stripSessionParam = () => {
        if (getAppCheckHealth() === 'refused') return;
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has(sessionParamName)) return;
        urlParams.delete(sessionParamName);
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      };

      // Process token exchange with optional ownership validation
      const processTokenExchange = async () => {
        try {
          // If user exists and shouldSignOut callback provided, validate ownership
          if (currentUser && shouldSignOut) {
            logger.debug('Validating session ownership', { uid: currentUser.uid });
            const needsSignOut = await shouldSignOut(currentUser.uid, sessionToken);

            if (!needsSignOut) {
              logger.debug('Session valid, keeping existing authentication');
              stripSessionParam();
              setIsProcessing(false);
              isProcessingTokenRef.current = false;

              if (onComplete) {
                onComplete(currentUser.uid);
              }
              return;
            }

            logger.debug('Session ownership mismatch, proceeding with sign-out');
          }

          // Sign out existing session if present (and validation passed or no callback)
          if (currentUser) {
            logger.debug('Signing out existing user before token exchange', { uid: currentUser.uid });
            await signOutMutation.mutateAsync();
            logger.debug('Existing session cleared');
          }

          // Exchange ID token for custom token
          const baseUrl = apiUrl || getApiUrl();
          const bypassSecret = getAutomationBypassSecret();
          const response = await fetch(`${baseUrl}/api/v1/auth/exchange-token`, { // wire-fold-ok: auth token exchange, reads only data.customToken/uid
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // Vercel deployment protection bypass (for cross-app requests in cloud environments)
              ...(bypassSecret && { 'x-vercel-protection-bypass': bypassSecret }),
            },
            body: JSON.stringify({ idToken: sessionToken }),
          });

          if (!response.ok) {
            throw new Error('Failed to exchange session token');
          }

          const data = await response.json();
          logger.debug('Token exchange successful');

          // Sign in with custom token using proper hook
          await signInWithTokenMutation.mutateAsync(data.data.customToken);

          logger.debug('Cross-app authentication complete', { uid: data.data.uid });
          stripSessionParam();
          setIsProcessing(false);
          isProcessingTokenRef.current = false; // Processing complete

          if (onComplete) {
            onComplete(data.data.uid);
          }
        } catch (err) {
          logger.error('Authentication failed', err);
          stripSessionParam();
          const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
          setError(errorMessage);
          setIsProcessing(false);
          isProcessingTokenRef.current = false; // Processing failed

          if (onError) {
            onError(err instanceof Error ? err : new Error(String(err)));
          }
        }
      };

      processTokenExchange();
    }
  }, [sessionToken, handoff, hasCredential, sessionParamName, currentUser, signInWithTokenMutation, signOutMutation, shouldSignOut, onComplete, onError, apiUrl, authCheckComplete]);

  // Show loading while auth check is running or processing token
  if (!authCheckComplete || isProcessing) {
    return <LoadingComponent />;
  }

  // Refused by App Check: the guest did nothing wrong, and only a reload can
  // run a fresh check (the session token is still in the URL — see
  // stripSessionParam — so the reload resumes the sign-in).
  if (error && getAppCheckHealth() === 'refused') {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4" role="alert">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            We couldn&apos;t verify this browser
          </h2>
          <p className="text-muted-foreground mb-6">
            This is on our side, not yours. Reloading the page usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // Show error if token processing failed or access denied
  if (error) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-destructive mb-4">
            Access Denied
          </h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <p className="text-sm text-muted-foreground">
            Please return to the restaurant menu and proceed to checkout from there.
          </p>
        </div>
      </div>
    );
  }

  // Render children when no token processing needed or complete
  return <>{children}</>;
}
